import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Activity,
  Building2,
  CalendarClock,
  Check,
  ChevronDown,
  CircleAlert,
  Clock3,
  CreditCard,
  FileDown,
  Maximize2,
  Minimize2,
  Plus,
  Search,
  ShieldCheck,
  Soup,
  UserRound,
  UsersRound,
  WifiOff,
} from "lucide-react";
import { clients, terminalGroups } from "../data";
import { useOpsSnapshot } from "../hooks/useOps";
import { useSdkEvents } from "../hooks/useSdkEvents";
import type { MealTagEvent, RestaurantRollup } from "../types";

function WaitingValue({ unit }: { unit?: string }) {
  return (
    <span className="waiting-value">
      —{unit ? ` ${unit}` : ""}
      <small>연결 대기</small>
    </span>
  );
}

const formatNumber = (value: number) => new Intl.NumberFormat("ko-KR").format(value);

const formatEventTime = (value: string) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  const part = (type: "month" | "day" | "hour" | "minute" | "second") =>
    parts.find((item) => item.type === type)?.value ?? "";
  return `${part("month")}.${part("day")} ${part("hour")}:${part("minute")}:${part("second")}`;
};

const formatUserId = (value: string) =>
  /^\d+$/.test(value) ? value.replace(/^0+(?=\d)/, "") : value;

function TagEventRow({ event, compact = false }: { event: MealTagEvent; compact?: boolean }) {
  const affiliation = [event.customer, event.team].filter(Boolean).join(" · ");
  const registeredUser = Boolean(event.userId && !/^0+$/.test(event.userId) && event.userId !== "unknown");
  const outcome = event.followUpStatus === "waiting"
    ? "재태그 대기"
    : event.followUpStatus === "resolved"
      ? "재태그 후 정상"
      : event.followUpStatus === "unresolved"
        ? "미해결 거부"
        : event.result;
  const outcomeClass = event.followUpStatus === "waiting"
    ? "pending"
    : event.followUpStatus === "resolved" || /정상|승인|success|ok/i.test(event.result)
      ? "success"
      : "notice";
  return (
    <div className={`event-row ${compact ? "compact" : ""}`}>
      <span>{formatEventTime(event.occurredAt)}</span>
      <strong>{event.userName || "미등록"}</strong>
      <span>{event.userId ? formatUserId(event.userId) : "—"}</span>
      {!compact && <span>{affiliation || (registeredUser ? "분류 확인 필요" : "미등록 사용자")}</span>}
      {!compact && <span>{event.corner}</span>}
      {!compact && <em className={outcomeClass}>{outcome}</em>}
    </div>
  );
}

function DataValue({
  value,
  unit,
  available = true,
}: {
  value: number;
  unit?: string;
  available?: boolean;
}) {
  if (!available) return <WaitingValue unit={unit} />;
  return (
    <span className="waiting-value live-value">
      {formatNumber(value)}{unit ? ` ${unit}` : ""}
      <small>원장 반영</small>
    </span>
  );
}

const restaurantValue = (
  restaurants: RestaurantRollup[] | undefined,
  pattern: RegExp,
  field: "amount" | "total" | "breakfast" | "lunch" | "dinner",
) =>
  Array.isArray(restaurants)
    ? restaurants
        .filter((row) => pattern.test(row.restaurant))
        .reduce((sum, row) => sum + Number(row[field] ?? 0), 0)
    : 0;

function SectionHeader({ title, meta, action }: { title: string; meta?: string; action?: string | ReactNode }) {
  return (
    <div className="section-header">
      <div>
        <h2>{title}</h2>
        {meta && <span>{meta}</span>}
      </div>
      {typeof action === "string" ? <button className="text-button">{action}</button> : action}
    </div>
  );
}

export function ControlView() {
  const { data, state } = useOpsSnapshot();
  const sdk = useSdkEvents();
  const available = Boolean(data);
  const buffetTicket = data ? restaurantValue(data.restaurants, /한식/, "amount") : 0;
  const hangoverTicket = data ? restaurantValue(data.restaurants, /해장/, "amount") : 0;
  const chineseTicket = data ? restaurantValue(data.restaurants, /중화|중식/, "amount") : 0;
  const buffet = buffetTicket + (data?.today.lunchbox.amount ?? 0) + (data?.today.kis.amount ?? 0);
  const chinese = chineseTicket + (data?.today.pos.chinese ?? 0);
  const hangover = hangoverTicket + (data?.today.pos.hangover ?? 0);
  const total = (data?.today.sikkwon.amount ?? 0)
    + (data?.today.lunchbox.amount ?? 0)
    + (data?.today.pos.chinese ?? 0)
    + (data?.today.pos.hangover ?? 0)
    + (data?.today.kis.amount ?? 0);
  const forecast = data?.forecast as {
    expectedDinner?: number;
    corners?: Array<{ corner?: string; expected?: number }>;
  } | null;
  const cornerForecast = (pattern: RegExp) =>
    forecast?.corners?.find((corner) => pattern.test(String(corner.corner ?? "")))?.expected ?? 0;

  return (
    <>
      <section className="hero hero-control">
        <div>
          <span className="hero-kicker">오늘 운영 요약</span>
          <h2>필요한 정보가 한 화면에 모입니다.</h2>
          <p>식권·도시락·POS·KIS는 단일 수집 관문을 통해 같은 원장에 반영됩니다.</p>
        </div>
        <div className="hero-side">
          <span>운영 상태</span>
          <strong>{state === "connected" ? "원천 연결 정상" : state === "waiting" ? "원천 연결 중" : "원천 상태 확인"}</strong>
          <em>{data?.businessDate ?? "가상 수치 미사용"}</em>
        </div>
      </section>

      <div className="metric-grid four">
        {[
          ["한식부페", "식권·도시락·KIS", "green", buffet],
          ["중화요리", "식권·POS", "red", chinese],
          ["해장밥상", "식권·POS", "amber", hangover],
          ["전체 매출", "오늘 누적", "navy", total],
        ].map(([label, meta, tone, value]) => (
          <article className={`metric-card ${tone}`} key={String(label)}>
            <span>{label}</span>
            <DataValue value={Number(value)} unit="원" available={available} />
            <small>{meta}</small>
          </article>
        ))}
      </div>

      <div className="content-grid split">
        <article className="panel">
          <SectionHeader title="실시간 태그" meta="SDK 이벤트" action="전체 보기" />
          {sdk.events.length ? (
            <div className="compact-event-list">
              {sdk.events.slice(0, 4).map((event) => <TagEventRow event={event} compact key={event.sourceEventId} />)}
            </div>
          ) : (
            <div className="empty-state compact">
              <WifiOff size={28} />
              <strong>{sdk.state === "connected" ? "개별 태그는 관리자 보호 중" : "식권 SDK 이벤트 연결 대기"}</strong>
              <span>{sdk.message}</span>
            </div>
          )}
        </article>
        <article className="panel">
          <SectionHeader title="고객사 식수 흐름" meta="조식 기준 인원 변화" />
          <div className="client-flow-list">
            {(data?.customersToday.length ? data.customersToday.slice(0, 4) : clients.slice(0, 4)).map((client) => (
              <div key={"customer" in client ? client.customer : client.id}>
                <span>{"customer" in client ? client.customer : client.name}</span>
                <div className="mini-track"><i /></div>
                <DataValue
                  value={"breakfast" in client ? client.breakfast : 0}
                  unit="명"
                  available={"breakfast" in client}
                />
              </div>
            ))}
          </div>
        </article>
      </div>

      <article className="panel">
        <SectionHeader title="오늘 중식·석식 예측" meta="실제 데이터가 충분할 때만 계산" />
        <div className="prediction-strip">
          <div><span>중식 도시락 확정</span><DataValue value={data?.today.lunchbox.quantity ?? 0} unit="개" available={available} /></div>
          <div><span>한식부페 석식 예측</span><DataValue value={cornerForecast(/한식|buffet/i)} unit="명" available={Boolean(forecast)} /></div>
          <div><span>해장밥상 석식 예측</span><DataValue value={cornerForecast(/해장|hangover/i)} unit="명" available={Boolean(forecast)} /></div>
          <div><span>중화요리 석식 예측</span><DataValue value={cornerForecast(/중화|chinese/i)} unit="명" available={Boolean(forecast)} /></div>
        </div>
      </article>
    </>
  );
}

export function LiveView() {
  const { data } = useOpsSnapshot();
  const sdk = useSdkEvents();
  const [adminKey, setAdminKey] = useState("");
  const [eventsFullscreen, setEventsFullscreen] = useState(false);
  const eventPanelRef = useRef<HTMLElement>(null);
  const sdkConnected = sdk.state === "connected";

  useEffect(() => {
    const syncFullscreenState = () => {
      setEventsFullscreen(document.fullscreenElement === eventPanelRef.current);
    };
    const closeFallbackFullscreen = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !document.fullscreenElement) setEventsFullscreen(false);
    };
    document.addEventListener("fullscreenchange", syncFullscreenState);
    document.addEventListener("keydown", closeFallbackFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      document.removeEventListener("keydown", closeFallbackFullscreen);
    };
  }, []);

  const toggleEventsFullscreen = async () => {
    const panel = eventPanelRef.current;
    if (!panel) return;
    if (eventsFullscreen) {
      if (document.fullscreenElement === panel && document.exitFullscreen) await document.exitFullscreen();
      else setEventsFullscreen(false);
      return;
    }
    if (panel.requestFullscreen) {
      try {
        await panel.requestFullscreen();
        return;
      } catch {
        // 제한된 모바일 브라우저에서는 CSS 전체화면으로 이어갑니다.
      }
    }
    setEventsFullscreen(true);
  };

  const terminalCount = (ids: string) => {
    const wanted = ids.split("·");
    return sdk.terminals
      .filter((row) => wanted.includes(String(row.terminalId)))
      .reduce((sum, row) => sum + Number(row.count ?? 0), 0);
  };
  const countFor = (label: string) => {
    if (!data) return 0;
    if (label === "한식부페") return restaurantValue(data.restaurants, /한식/, "total");
    if (label === "해장밥상") return restaurantValue(data.restaurants, /해장/, "total");
    return restaurantValue(data.restaurants, /중화|중식/, "total");
  };

  return (
    <>
      <div className="terminal-grid">
        {terminalGroups.map((group) => (
          <article className={`terminal-card ${group.tone}`} key={group.ids}>
            <div className="terminal-top">
              <span>{group.ids}</span>
              <em className={sdkConnected ? "connected" : ""}><i /> {sdkConnected ? "SDK 실시간" : "SQL 집계"}</em>
            </div>
            <h3>{group.label}</h3>
            <DataValue
              value={sdkConnected ? terminalCount(group.ids) : countFor(group.label)}
              unit="건"
              available={sdkConnected || Boolean(data)}
            />
            <small>{sdkConnected ? "오늘 누적 · 개별 이벤트 2초 갱신" : "오늘 누적 · SDK 연결 전 SQL 집계"}</small>
            <div className="signal-bars"><i /><i /><i /><i /></div>
          </article>
        ))}
      </div>

      <div className="content-grid live-layout">
        <article className={`panel event-panel ${eventsFullscreen ? "events-fullscreen" : ""}`} ref={eventPanelRef}>
          <SectionHeader
            title="전체 태그 이벤트"
            meta="최신 이벤트가 위에 표시됩니다."
            action={(
              <button
                className="fullscreen-button"
                type="button"
                onClick={() => void toggleEventsFullscreen()}
                aria-label={eventsFullscreen ? "전체 태그 이벤트 전체화면 종료" : "전체 태그 이벤트 전체화면 보기"}
                title={eventsFullscreen ? "전체화면 종료" : "전체화면 보기"}
              >
                {eventsFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                <span>{eventsFullscreen ? "전체화면 종료" : "전체화면 보기"}</span>
              </button>
            )}
          />
          <div className="table-head events">
            <span>인증시각</span><span>근로자</span><span>사용자 ID</span><span>고객사·팀</span><span>코너</span><span>결과</span>
          </div>
          {sdk.events.length ? (
            <>
              <div className="event-list">
                {sdk.events.map((event) => <TagEventRow event={event} key={event.sourceEventId} />)}
              </div>
              <div className="event-access-bar">
                <span>관리자 보호 조회 중</span>
                <button type="button" onClick={sdk.lock}>잠금</button>
              </div>
            </>
          ) : (
            <div className="empty-state table-empty">
              <Activity size={30} />
              <strong>{sdk.state === "connected" ? "실시간 집계 연결 · 개별 이벤트 보호" : "개별 태그 이벤트 연결 대기"}</strong>
              <span>{sdk.message}</span>
              {sdk.state === "connected" && sdk.personalEventsProtected && (
                <form
                  className="event-auth-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    sdk.unlock(adminKey);
                    setAdminKey("");
                  }}
                >
                  <label>
                    <span>관리자 비밀번호</span>
                    <input
                      aria-label="관리자 비밀번호"
                      type="password"
                      autoComplete="current-password"
                      value={adminKey}
                      onChange={(event) => setAdminKey(event.target.value)}
                      placeholder="관리자 비밀번호를 입력하세요"
                    />
                  </label>
                  <button className="primary-button" type="submit" disabled={!adminKey.trim()}>
                    관리자 인증
                  </button>
                  {sdk.hasViewToken && <small>관리자 비밀번호가 일치하지 않습니다.</small>}
                </form>
              )}
            </div>
          )}
        </article>
        <article className="panel">
          <SectionHeader title="최근 30분 흐름" meta="1분 단위" />
          <div className="chart-placeholder">
            <div className="chart-grid" />
            {sdk.flow.length ? (
              <div className="live-flow-bars">
                {sdk.flow.slice(-30).map((point, index) => (
                  <i
                    className={point.corner}
                    key={`${point.minute}-${point.corner}-${index}`}
                    style={{ height: `${Math.max(8, Math.min(100, Number(point.count) * 8))}%` }}
                    title={`${point.minute} ${point.corner} ${point.count}건`}
                  />
                ))}
              </div>
            ) : <div className="chart-zero">연결 후 태그 흐름 표시</div>}
          </div>
          <div className="legend">
            <span><i className="green" />한식부페</span>
            <span><i className="amber" />해장밥상</span>
            <span><i className="red" />중화요리</span>
          </div>
        </article>
      </div>
    </>
  );
}

export function SalesView() {
  const { data } = useOpsSnapshot();
  const monthTotal = data?.month.reduce((sum, row) => sum + Number(row.amount ?? 0), 0) ?? 0;
  const buffetTicket = data ? restaurantValue(data.restaurants, /한식/, "amount") : 0;
  const hangoverTicket = data ? restaurantValue(data.restaurants, /해장/, "amount") : 0;
  const chineseTicket = data ? restaurantValue(data.restaurants, /중화|중식/, "amount") : 0;
  const cornerRows = [
    {
      label: "한식부페",
      meta: "식권·도시락·KIS",
      ticket: buffetTicket,
      secondary: (data?.today.lunchbox.amount ?? 0) + (data?.today.kis.amount ?? 0),
    },
    {
      label: "중화요리",
      meta: "식권·POS",
      ticket: chineseTicket,
      secondary: data?.today.pos.chinese ?? 0,
    },
    {
      label: "해장밥상",
      meta: "식권·POS",
      ticket: hangoverTicket,
      secondary: data?.today.pos.hangover ?? 0,
    },
  ];

  return (
    <>
      <section className="hero hero-sales">
        <div>
          <span className="hero-kicker">이번 달 누적 매출</span>
          <h2><DataValue value={monthTotal} unit="원" available={Boolean(data)} /></h2>
          <p>식권카드·도시락·POS·KIS의 검증된 집계만 합산합니다.</p>
        </div>
        <button className="light-button"><FileDown size={17} /> 월간 보고서</button>
      </section>
      <div className="metric-grid three">
        {cornerRows.map((row) => (
          <article className="metric-card detailed" key={row.label}>
            <div><span>{row.label}</span><em>{row.meta}</em></div>
            <DataValue value={row.ticket + row.secondary} unit="원" available={Boolean(data)} />
            <div className="source-lines">
              <span>식권카드 <b>{data ? `${formatNumber(row.ticket)}원` : "—"}</b></span>
              <span>{row.label === "한식부페" ? "도시락·KIS" : "POS"} <b>{data ? `${formatNumber(row.secondary)}원` : "—"}</b></span>
            </div>
          </article>
        ))}
      </div>
      <div className="content-grid split">
        <article className="panel">
          <SectionHeader title="최근 일별 매출" meta="1일부터 날짜순" />
          {data?.daily.length ? (
            <div className="simple-list">
              {data.daily.map((row) => (
                <div key={row.business_date}>
                  <strong>{row.business_date}</strong>
                  <span>{formatNumber(Number(row.amount))}원</span>
                </div>
              ))}
            </div>
          ) : <div className="empty-state compact"><BarEmpty /></div>}
        </article>
        <article className="panel">
          <SectionHeader title="고객사 정산 누적" meta="식권카드+도시락" />
          <div className="simple-list">
            {(data?.customersMonth.length ? data.customersMonth : clients).map((client) => (
              <div key={"customer" in client ? client.customer : client.id}>
                <strong>{"customer" in client ? client.customer : client.name}</strong>
                {"settlement" in client
                  ? <span>{formatNumber(Number(client.settlement))}원</span>
                  : <WaitingValue unit="원" />}
              </div>
            ))}
          </div>
        </article>
      </div>
    </>
  );
}

function BarEmpty() {
  return (
    <>
      <BarChartIcon />
      <strong>매출 원장 연결 대기</strong>
      <span>확인된 매출만 날짜별로 표시합니다.</span>
    </>
  );
}

function BarChartIcon() {
  return (
    <div className="bar-icon" aria-hidden="true">
      <i /><i /><i /><i />
    </div>
  );
}

export function ClientsView() {
  const { data } = useOpsSnapshot();

  return (
    <>
      <div className="toolbar">
        <div className="search-box"><Search size={17} /><input aria-label="고객사 검색" placeholder="고객사 또는 팀 검색" /></div>
        <button className="primary-button"><Plus size={17} /> 고객사 등록</button>
      </div>
      <div className="client-card-grid">
        {clients.map((client) => {
          const today = data?.customersToday.find((row) => row.customer === client.name);
          return (
          <article className="client-card" key={client.id}>
            <div className="client-card-head">
              <span className="company-icon"><Building2 size={19} /></span>
              <div><h3>{client.name}</h3><span>{client.teams.length}개 팀·부서</span></div>
              <button aria-label={`${client.name} 펼치기`}><ChevronDown size={18} /></button>
            </div>
            <div className="client-stats">
              <div><span>오늘 조식</span><DataValue value={today?.breakfast ?? 0} unit="명" available={Boolean(today)} /></div>
              <div><span>오늘 중식</span><DataValue value={(today?.lunch ?? 0) + (today?.lunchboxQuantity ?? 0)} unit="명" available={Boolean(today)} /></div>
              <div><span>오늘 석식</span><DataValue value={today?.dinner ?? 0} unit="명" available={Boolean(today)} /></div>
            </div>
            <div className="team-tags">
              {client.teams.length ? client.teams.slice(0, 4).map((team) => <span key={team}>{team}</span>) : <span>직속 고객사</span>}
              {client.teams.length > 4 && <span>+{client.teams.length - 4}</span>}
            </div>
          </article>
        )})}
      </div>
      <aside className="insight-card">
        <span className="ai-mark">AI</span>
        <div><strong>고객사 변화 분석 준비 중</strong><p>3일 이상 식권 원장이 축적되면 인원 증감과 이용 변화를 분석합니다.</p></div>
      </aside>
    </>
  );
}

export function LunchboxView() {
  const { data } = useOpsSnapshot();

  return (
    <>
      <section className="hero hero-lunchbox">
        <div>
          <span className="hero-kicker">오늘 도시락 주문</span>
          <h2>도시락 주문을 운영센터에서 바로 입력합니다.</h2>
          <p>고객사 통보 수량을 입력하면 생산 준비와 월정산 원장에 한 번만 반영됩니다.</p>
        </div>
        <div className="hero-side"><span>오늘 확정</span><DataValue value={data?.today.lunchbox.quantity ?? 0} unit="개" available={Boolean(data)} /></div>
      </section>
      <div className="content-grid lunchbox-layout">
        <article className="panel order-form">
          <SectionHeader title="고객사별 주문 입력" meta="확정 전 수정 가능" />
          <label>고객사<select><option>고객사를 선택하세요</option>{clients.map((c) => <option key={c.id}>{c.name}</option>)}</select></label>
          <div className="field-row">
            <label>점심 수량<input type="number" min="0" placeholder="0" /></label>
            <label>석식 수량<input type="number" min="0" placeholder="0" /></label>
          </div>
          <label>전달사항<textarea placeholder="알레르기, 배송 위치 등 필요한 내용" /></label>
          <button className="primary-button full" disabled>입력 기능 연결 예정</button>
        </article>
        <article className="panel">
          <SectionHeader title="생산 준비" meta="확정 주문 합계" />
          <div className="production-total"><Soup size={34} /><div><DataValue value={data?.today.lunchbox.quantity ?? 0} unit="개" available={Boolean(data)} /><span>{data ? "주문 원장 반영" : "원장 연결 후 표시"}</span></div></div>
          <div className="notice-row"><CalendarClock size={19} /><div><strong>토요일 단축운영</strong><span>점심·도시락만 운영, 13:00 종료</span></div></div>
          <div className="empty-state compact"><ClipboardEmpty /></div>
        </article>
      </div>
    </>
  );
}

function ClipboardEmpty() {
  return <><CircleAlert size={27} /><strong>확정된 주문이 없습니다.</strong><span>주문 원장이 연결되면 고객사별 내역이 표시됩니다.</span></>;
}

export function StaffView() {
  return (
    <>
      <div className="staff-summary">
        <div><UsersRound size={22} /><span>등록 근로자</span><WaitingValue unit="명" /></div>
        <div><CreditCard size={22} /><span>활성 카드</span><WaitingValue unit="장" /></div>
        <div><ShieldCheck size={22} /><span>오늘 정상 태그</span><WaitingValue unit="건" /></div>
      </div>
      <div className="content-grid staff-layout">
        <article className="panel tree-panel">
          <SectionHeader title="고객사·팀 구조" meta="다이젯 원장 기준" />
          <div className="tree-list">
            {clients.map((client) => (
              <details key={client.id}>
                <summary><Building2 size={16} />{client.name}<span>{client.teams.length}</span></summary>
                {client.teams.map((team) => <button key={team}>{team}</button>)}
              </details>
            ))}
          </div>
        </article>
        <article className="panel user-list-panel">
          <SectionHeader title="사용자·카드 현황" meta="개인정보 권한 필요" />
          <div className="empty-state">
            <UserRound size={33} />
            <strong>직원 원장 연결 대기</strong>
            <span>이름·사용자 ID·카드 상태는 인증된 사용자에게만 표시됩니다.</span>
          </div>
        </article>
        <article className="panel profile-panel">
          <SectionHeader title="선택 사용자 상세" />
          <div className="profile-empty"><UserRound size={30} /><span>사용자를 선택하세요.</span></div>
          <button className="outline-button full" disabled>개인별 태그 횟수 설정</button>
        </article>
      </div>
    </>
  );
}

export function ForecastView() {
  const { data } = useOpsSnapshot();
  const forecast = data?.forecast as {
    expectedDinner?: number;
    range?: { confidence?: number };
    corners?: Array<{ corner?: string; expected?: number }>;
  } | null;
  const cornerValue = (pattern: RegExp) =>
    forecast?.corners?.find((corner) => pattern.test(String(corner.corner ?? "")))?.expected ?? 0;

  return (
    <>
      <section className="forecast-hero">
        <span>오늘 중식 예측</span>
        <h2><DataValue value={forecast?.expectedDinner ?? 0} unit="명" available={Boolean(forecast)} /></h2>
        <p>최근 3일 조식 대비 중식 비율과 확정 도시락 수량을 결합합니다.</p>
        <div><span>예측 신뢰도</span><strong>{forecast?.range?.confidence ? `${Math.round(forecast.range.confidence * 100)}%` : "학습 대기"}</strong></div>
      </section>
      <div className="content-grid split">
        <article className="panel">
          <SectionHeader title="최근 3일 조식 대비 중식 비교" meta="고객사별 가중 평균" />
          <div className="ratio-grid">
            {[3, 2, 1].map((day) => <div key={day}><span>{day}일 전</span><i /><WaitingValue unit="%" /></div>)}
          </div>
        </article>
        <article className="panel">
          <SectionHeader title="석식 코너별 예측" meta="최근 7일 개인별 이용 성향" />
          <div className="forecast-list">
            {terminalGroups.map((group) => {
              const pattern = group.label === "한식부페" ? /한식|buffet/i : group.label === "해장밥상" ? /해장|hangover/i : /중화|chinese/i;
              return <div key={group.ids}><span><i className={group.tone} />{group.label}</span><DataValue value={cornerValue(pattern)} unit="명" available={Boolean(forecast)} /></div>;
            })}
          </div>
        </article>
      </div>
      <article className="panel">
        <SectionHeader title="예측 판단 기준" />
        <div className="method-grid">
          <div><span>01</span><strong>조식 출근지표</strong><p>당일 조식 태그를 출근 인원의 기준점으로 사용</p></div>
          <div><span>02</span><strong>중식 도시락 비율</strong><p>최근 3일 조식 대비 실제 중식·도시락 이용률 반영</p></div>
          <div><span>03</span><strong>석식 코너 성향</strong><p>최근 7일 근로자별 코너 이용 비율을 고객사별로 계산</p></div>
        </div>
      </article>
    </>
  );
}

export function MenuView() {
  return (
    <>
      <div className="menu-toolbar">
        <div><button>‹</button><strong>2026년 7월</strong><button>›</button></div>
        <span>한식부페 전용</span>
        <button className="outline-button"><FileDown size={16} /> 월간 식단표 출력</button>
      </div>
      <section className="menu-hero">
        <div><span>계절 운영 기준</span><h2>하절기 · 건설현장 근로자</h2><p>수분·염분 보충과 식중독 위험을 함께 고려해 한 달 식단을 구성합니다.</p></div>
        <div><strong>토요일</strong><span>점심·도시락만</span><strong>일요일</strong><span>휴무</span></div>
      </section>
      <div className="calendar-panel">
        <div className="calendar-week">{["일", "월", "화", "수", "목", "금", "토"].map((day) => <span key={day}>{day}</span>)}</div>
        <div className="calendar-grid">
          {Array.from({ length: 35 }, (_, index) => {
            const day = index - 2;
            const inMonth = day > 0 && day <= 31;
            const isSunday = index % 7 === 0;
            const isSaturday = index % 7 === 6;
            return (
              <button key={index} className={`${!inMonth ? "outside" : ""} ${isSunday ? "closed" : ""}`}>
                <span>{inMonth ? day : ""}</span>
                {inMonth && isSunday ? <em>휴무</em> : inMonth && isSaturday ? <em>중식·도시락</em> : inMonth ? <small>식단 미작성</small> : null}
              </button>
            );
          })}
        </div>
      </div>
      <p className="menu-note">식단은 다른 매출·재고·발주 시스템과 연동하지 않는 독립 작성 기능입니다.</p>
    </>
  );
}

export function SystemView() {
  const { sources } = useOpsSnapshot();
  return (
    <>
      <div className="health-grid">
        {sources.map((source) => (
          <article className="health-card" key={source.id}>
            <div><span className={`status-dot ${source.state}`} /><strong>{source.label}</strong></div>
            <h3>{source.state === "connected" ? "정상" : source.state === "delayed" ? "지연" : source.state === "error" ? "확인 필요" : "연결 대기"}</h3>
            <p>{source.message}</p>
            <span>마지막 성공 {source.updatedAt ? new Date(source.updatedAt).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : "—"}</span>
          </article>
        ))}
      </div>
      <div className="content-grid split">
        <article className="panel">
          <SectionHeader title="최근 운영 기록" meta="오류와 복구 이력" />
          <div className="empty-state compact"><Clock3 size={28} /><strong>운영 기록이 없습니다.</strong><span>서비스 연결 이후 감사 가능한 이력이 쌓입니다.</span></div>
        </article>
        <article className="panel">
          <SectionHeader title="운영 원칙" />
          <ul className="principle-list">
            <li><Check size={16} />한 원천 실패가 다른 원천을 중단시키지 않음</li>
            <li><Check size={16} />화면 날짜는 한국시간으로 독립 전환</li>
            <li><Check size={16} />실패 회차는 기록하고 다음 회차 진행</li>
            <li><Check size={16} />가짜 정상 상태와 가상 매출 금지</li>
          </ul>
        </article>
      </div>
    </>
  );
}
